import { useState, type ReactNode } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import HistoryIcon from "@mui/icons-material/History";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useMatch,
  useParams,
} from "react-router-dom";
import type { Promisable } from "type-fest";

import type {
  Assessment,
  Person,
  PersonInvolvement,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { PersonBanner } from "../../components/PersonBanner/PersonBanner.js";
import { createRoute } from "../../routing/createRoute.js";
import { AssessmentActivityDialog } from "./AssessmentActivityDialog.js";
import { AssessmentProvider, useAssessment } from "./AssessmentContext.js";
import { overviewHeadsRecordOf } from "./AssessmentOverview.js";
import { AssessmentRecordTitle } from "./AssessmentRecordTitle.js";

/** Why an assessment could not be read, which is all its layout then shows. */
export type AssessmentReadFailure = "not_found" | "unavailable";

/**
 * Whether the layout heads the screen with the assessment record, or the
 * screen draws it among its own controls.
 */
export type AssessmentRecordHeadedBy = "layout" | "screen";

/**
 * The frame around every screen of one assessment: whose it is and the way
 * back to the young person's assessments, headed by the assessment record
 * unless the screen heads it itself (`AssessmentRecordHeadedBy`).
 *
 * Holds the assessment open in an `AssessmentProvider`, keyed by its id, for
 * the screens inside it to share.
 */
export const AssessmentLayout = () => {
  const { assessmentId } = useParams();
  const location: { state: unknown } = useLocation();

  // `useParams` types every segment as optional, but the route cannot match
  // without this one.
  const requestedId = assessmentId ?? "";

  return (
    <AssessmentProvider
      key={requestedId}
      requestedId={requestedId}
      carryOverAsked={carryOverAskedIn(location.state)}
    >
      <AssessmentLayoutFor />
    </AssessmentProvider>
  );
};

/**
 * Whether the start that led here asked to carry the earlier assessment's
 * ratings over. Read from navigation state, which anything could have put
 * there, so only an explicit `true` counts.
 */
export const carryOverAskedIn = (state: unknown): boolean =>
  typeof state === "object" &&
  state !== null &&
  "carryOver" in state &&
  state.carryOver === true;

const AssessmentLayoutFor = () => {
  const { assessment, opening } = useAssessment();

  if (assessment === undefined) {
    return (
      <AssessmentLayoutContent
        assessment={undefined}
        failure={
          opening.kind === "failed" && opening.reason !== "not_open"
            ? opening.reason
            : undefined
        }
        person={undefined}
        involvements={undefined}
      >
        <Outlet />
      </AssessmentLayoutContent>
    );
  }

  return <AssessmentLayoutAbout assessment={assessment} />;
};

/** The layout once the assessment is read, and so whose it is known. */
const AssessmentLayoutAbout = ({ assessment }: { assessment: Assessment }) => {
  const [{ data: person, response: personResponse }] = apiHooks.person.byId(
    assessment.personId,
  );
  const [{ data: involvements, response: involvementsResponse }] =
    apiHooks.involvement.forPerson(assessment.personId);
  const [showingActivity, setShowingActivity] = useState(false);
  const overviewHeadsRecord =
    useMatch(createRoute.assessment(assessment.id)) !== null &&
    overviewHeadsRecordOf(assessment);

  return (
    <>
      <AssessmentLayoutContent
        assessment={assessment}
        recordHeadedBy={overviewHeadsRecord ? "screen" : "layout"}
        person={personResponse?.status === 200 ? person : undefined}
        involvements={
          involvementsResponse?.status === 200 ? involvements : undefined
        }
        onShowActivity={() => {
          setShowingActivity(true);
        }}
      >
        <Outlet />
      </AssessmentLayoutContent>
      {showingActivity ? (
        <AssessmentActivityDialog
          assessment={assessment}
          onClose={() => {
            setShowingActivity(false);
          }}
        />
      ) : null}
    </>
  );
};

/**
 * The layout's rendering, with everything it shows injected so a story or a
 * test can show any state of it without an API client.
 *
 * Until the assessment is read nothing of it shows, and an assessment that
 * cannot be read shows only why: someone who may not open it learns nothing
 * about whose it is from the frame around it. One whose answers were withheld
 * shows the frame and says so in place of the screen, which would otherwise
 * show it unanswered.
 */
export const AssessmentLayoutContent = ({
  assessment,
  failure,
  person,
  involvements,
  recordHeadedBy = "layout",
  onShowActivity,
  children,
}: {
  assessment: Assessment | undefined;
  failure?: AssessmentReadFailure | undefined;
  /** The young person, for the banner; it is left out until read. */
  person: Person | undefined;
  /** Who is involved with them, for the banner. */
  involvements: PersonInvolvement[] | undefined;
  recordHeadedBy?: AssessmentRecordHeadedBy | undefined;
  /** Shows every act on the assessment; absent where the log is not offered. */
  onShowActivity?: (() => Promisable<void>) | undefined;
  /** The screen inside the layout. */
  children: ReactNode;
}) => {
  const { t } = useTranslation();

  if (assessment === undefined) {
    return failure === undefined ? (
      <Typography variant="body2" color="text.secondary">
        {t("common.loading")}
      </Typography>
    ) : (
      <Alert severity="error">{t(`assessment.page.failed.${failure}`)}</Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          // Above the screen's own controls as they scroll beneath, and below
          // the app's bars, drawers and dialogs.
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: "background.default",
        }}
      >
        <Stack spacing={1}>
          {person === undefined ? null : (
            <PersonBanner person={person} involvements={involvements ?? []} />
          )}
          <Button
            component={RouterLink}
            to={createRoute.personTab(assessment.personId, "assessments")}
            startIcon={<ChevronLeftIcon />}
            size="small"
            sx={{
              alignSelf: "flex-start",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {t("assessment.layout.back")}
          </Button>
          {recordHeadedBy === "screen" ? null : (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <AssessmentRecordTitle assessment={assessment} />
              {onShowActivity === undefined ? null : (
                <Button
                  size="small"
                  startIcon={<HistoryIcon />}
                  onClick={() => void onShowActivity()}
                >
                  {t("personPage.assessments.activity.open")}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
      {assessment.answersWithheld === true ? (
        <Alert severity="info">{t("assessment.page.contentsWithheld")}</Alert>
      ) : (
        children
      )}
    </Stack>
  );
};
